import { useEffect, useState } from "react";
import { useParams } from "react-router";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { backendBase } from "@/api/backendBase";

interface IssueCounts {
  computed: number;
  groundTruth: number;
  truePositives: number;
  falsePositives: number;
  falseNegatives: number;
  precisionPct: number;
  recallPct: number;
}

interface RuleIssueStat {
  ruleId: string;
  fires: number;
  truePositives: number;
  falsePositives: number;
  truePositivePct: number;
}

interface OrderSample {
  orderNumber: string;
  ruleId?: string | null;
}

interface ColumnAccuracyStat {
  column: string;
  accurate: number;
  total: number;
  accuracyPct: number;
}

interface AnalyseReport3Response {
  date: string;
  reportFile: string;
  sourceRowCounts: Record<string, number>;
  issueCounts: IssueCounts;
  byRule: RuleIssueStat[];
  columnAccuracy: ColumnAccuracyStat[];
  falsePositiveSamples: OrderSample[];
  falseNegativeSamples: OrderSample[];
  findings: string[];
}

type MuiColor = "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";

const pctColor = (pct: number): MuiColor => {
  if (pct >= 90) return "success";
  if (pct >= 50) return "warning";
  return "error";
};

export const Analysis3 = () => {
  const params = useParams();
  const date = params.date;

  const [data, setData] = useState<AnalyseReport3Response | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!date) return;
    setData(null);
    setError(null);
    fetch(`${backendBase()}/sandbox/analyse-report-3?date=${date}`)
      .then((res) => res.json())
      .then((json) => {
        if (json.error) {
          setError(json.error);
          return;
        }
        setData(json);
      })
      .catch((e) => setError(String(e)));
  }, [date]);

  if (!date) {
    return <Alert severity="error">Missing date in URL — expected /sandbox/analysis3/&lt;date&gt;</Alert>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        maxWidth: 1200,
        mx: "auto",
      }}
    >
      <Typography variant="h4" gutterBottom>
        Issue-Set Analysis — {data.date}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Report file: {data.reportFile}. Compares only which Order #s the rules engine flags "Issue" against the Order #s
        present in the ground-truth fixture — computed Status values are never compared.
      </Typography>

      <Stack
        direction="row"
        spacing={3}
        sx={{ mb: 4 }}
      >
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography
            variant="overline"
            color="text.secondary"
          >
            Computed vs Ground Truth
          </Typography>
          <Typography variant="h5">
            {data.issueCounts.computed} vs {data.issueCounts.groundTruth}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography
            variant="overline"
            color="text.secondary"
          >
            Precision
          </Typography>
          <Typography variant="h5">
            {data.issueCounts.truePositives} / {data.issueCounts.computed}
          </Typography>
          <Chip
            label={`${data.issueCounts.precisionPct}%`}
            color={pctColor(data.issueCounts.precisionPct)}
            size="small"
            sx={{ mt: 1 }}
          />
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography
            variant="overline"
            color="text.secondary"
          >
            Recall
          </Typography>
          <Typography variant="h5">
            {data.issueCounts.truePositives} / {data.issueCounts.groundTruth}
          </Typography>
          <Chip
            label={`${data.issueCounts.recallPct}%`}
            color={pctColor(data.issueCounts.recallPct)}
            size="small"
            sx={{ mt: 1 }}
          />
        </Paper>
      </Stack>

      <Typography
        variant="h6"
        gutterBottom
      >
        Findings
      </Typography>
      <Stack
        spacing={1}
        sx={{ mb: 4 }}
      >
        {data.findings.map((finding, i) => (
          <Alert
            key={i}
            severity="warning"
            variant="outlined"
          >
            {finding}
          </Alert>
        ))}
      </Stack>

      <Typography
        variant="h6"
        gutterBottom
      >
        By Rule (Issue-producing rules only)
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ mb: 4 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rule</TableCell>
              <TableCell align="right">Fires</TableCell>
              <TableCell align="right">True Positives</TableCell>
              <TableCell align="right">False Positives</TableCell>
              <TableCell align="right">TP %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.byRule.map((r) => (
              <TableRow key={r.ruleId}>
                <TableCell>{r.ruleId}</TableCell>
                <TableCell align="right">{r.fires}</TableCell>
                <TableCell align="right">{r.truePositives}</TableCell>
                <TableCell align="right">{r.falsePositives}</TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${r.truePositivePct}%`}
                    color={pctColor(r.truePositivePct)}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography
        variant="h6"
        gutterBottom
      >
        Column Accuracy (true-positive rows only)
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2 }}
      >
        Exact-string match rate per column, computed only over the {data.issueCounts.truePositives} Order #s both sides
        agree are an issue. A low % can mean a real data mismatch OR just a formatting/vocabulary difference (e.g. a date
        written "19-Jun-2026" vs "19/06/2026") — this table doesn't tell them apart, it's a starting point for
        spot-checking.
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ mb: 4 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Column</TableCell>
              <TableCell align="right">Accurate</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.columnAccuracy.map((c) => (
              <TableRow key={c.column}>
                <TableCell>{c.column}</TableCell>
                <TableCell align="right">{c.accurate}</TableCell>
                <TableCell align="right">{c.total}</TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${c.accuracyPct}%`}
                    color={pctColor(c.accuracyPct)}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography
        variant="h6"
        gutterBottom
      >
        False Positives — flagged "Issue", not in ground truth ({data.issueCounts.falsePositives})
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ mb: 4 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order #</TableCell>
              <TableCell>Rule</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.falsePositiveSamples.map((s) => (
              <TableRow key={s.orderNumber}>
                <TableCell>{s.orderNumber}</TableCell>
                <TableCell>{s.ruleId}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography
        variant="h6"
        gutterBottom
      >
        False Negatives — in ground truth, never flagged ({data.issueCounts.falseNegatives})
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order #</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.falseNegativeSamples.map((s) => (
              <TableRow key={s.orderNumber}>
                <TableCell>{s.orderNumber}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
